import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLoanCrudHooks } from './lib/create-crud-hooks';
import { api } from '@/lib/api';

export type CovenantType =
  | 'LTV_RATIO'
  | 'INTEREST_COVERAGE'
  | 'VALUATION_CURRENCY'
  | 'INSURANCE_CURRENT'
  | 'FINANCIAL_REPORTING'
  | 'DEVELOPMENT_MILESTONE'
  | 'CUSTOM';
export type CovenantStatus = 'ACTIVE' | 'BREACHED' | 'WAIVED' | 'EXPIRED' | 'MET';

export interface Covenant {
  id: string;
  loanId: string;
  type: CovenantType;
  name: string;
  description?: string;
  threshold?: string;
  frequency: string;
  status: CovenantStatus;
  lastChecked?: string;
  nextDue?: string;
  notes?: string;
  checks?: CovenantCheck[];
  createdAt: string;
}

export interface CovenantCheck {
  id: string;
  covenantId: string;
  checkDate: string;
  result: 'PASS' | 'FAIL' | 'WAIVED';
  value?: string;
  notes?: string;
  checkedBy?: { firstName: string; lastName: string };
}

export interface CovenantSummary {
  total: number;
  active: number;
  breached: number;
  complianceRate: number;
  nextDueDate?: string;
}

export const COVENANT_TYPE_LABELS: Record<CovenantType, string> = {
  LTV_RATIO: 'LTV Ratio',
  INTEREST_COVERAGE: 'Interest Coverage',
  VALUATION_CURRENCY: 'Valuation Currency',
  INSURANCE_CURRENT: 'Insurance Current',
  FINANCIAL_REPORTING: 'Financial Reporting',
  DEVELOPMENT_MILESTONE: 'Development Milestone',
  CUSTOM: 'Custom',
};

export const COVENANT_STATUS_CONFIG: Record<
  CovenantStatus,
  { label: string; color: string; bg: string }
> = {
  ACTIVE: { label: 'Active', color: '#3b82f6', bg: '#eff6ff' },
  BREACHED: { label: 'Breached', color: '#ef4444', bg: '#fef2f2' },
  WAIVED: { label: 'Waived', color: '#f59e0b', bg: '#fffbeb' },
  EXPIRED: { label: 'Expired', color: '#6b7280', bg: '#f9fafb' },
  MET: { label: 'Met', color: '#22c55e', bg: '#f0fdf4' },
};

// ── Factory CRUD hooks ───────────────────────────────────

const { useList } = createLoanCrudHooks<Covenant, any, any>('covenants', 'covenants');

export function useCovenants(loanId: string) {
  return useList(loanId);
}

// ── Summary query (manual) ───────────────────────────────

export function useCovenantSummary(loanId: string) {
  return useQuery<CovenantSummary>({
    queryKey: ['covenants', loanId, 'summary'],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/covenants/summary`);
      return data;
    },
    enabled: !!loanId,
  });
}

// ── Create covenant (manual - uses loanId param) ────────

export function useCreateCovenant(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: any) => {
      const { data } = await api.post(`/loans/${loanId}/covenants`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['covenants', loanId] });
    },
  });
}

// ── Check covenant (manual - custom action) ──────────────

export function useCheckCovenant(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ covenantId, ...input }: any) => {
      const { data } = await api.post(`/loans/${loanId}/covenants/${covenantId}/check`, {
        covenantId,
        ...input,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['covenants', loanId] });
    },
  });
}
